import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Trophy } from 'lucide-react';

const teams = [
  { name: 'Arsenal', logo: 'https://resources.premierleague.com/premierleague25/badges/3.svg' },
  { name: 'Chelsea', logo: 'https://resources.premierleague.com/premierleague25/badges/8.svg' },
  { name: 'Liverpool', logo: 'https://resources.premierleague.com/premierleague25/badges/14.svg' },
  { name: 'Man City', logo: 'https://resources.premierleague.com/premierleague25/badges/43.svg' },
  { name: 'Man Utd', logo: 'https://resources.premierleague.com/premierleague25/badges/1.svg' },
  { name: 'Spurs', logo: 'https://resources.premierleague.com/premierleague25/badges/6.svg' },
];

const previouslyPicked = ['Liverpool', 'Man City'];

export const LMSPickDemo: React.FC = () => {
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    const sequence = [
      () => { setSelectedTeam(null); setIsLocked(false); setShowConfirmation(false); },
      () => setSelectedTeam('Arsenal'),
      () => setIsLocked(true),
      () => setShowConfirmation(true),
    ];

    const timings = [0, 1200, 2200, 2600];
    
    const runSequence = () => {
      sequence.forEach((action, index) => {
        setTimeout(action, timings[index]);
      });
    };

    runSequence();
    const interval = setInterval(runSequence, 7000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col h-full pt-8 pb-4 px-2">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-3 rounded-t-xl -mx-2 -mt-8 mb-3">
        <div className="flex items-center space-x-2">
          <div className="bg-white/20 p-1.5 rounded-lg">
            <Trophy className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Gameweek 24</h3>
            <p className="text-white/80 text-[9px]">Pick a team to win</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mb-3">
        {teams.map((team) => {
          const isPreviouslyPicked = previouslyPicked.includes(team.name);
          const isSelected = selectedTeam === team.name;
          
          return (
            <motion.div
              key={team.name}
              className={`relative bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2 border transition-all ${
                isPreviouslyPicked ? 'border-gray-300 opacity-50' : 
                isSelected ? 'border-blue-500 ring-2 ring-blue-500/30 bg-blue-50' : 
                'border-gray-200/50'
              }`}
              animate={isSelected && isLocked ? { scale: [1, 1.05, 1] } : {}}
            >
              {isPreviouslyPicked && (
                <div className="absolute top-1 right-1">
                  <Lock className="h-2.5 w-2.5 text-gray-400" />
                </div>
              )}
              <AnimatePresence>
                {isSelected && isLocked && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center z-10"
                  >
                    <Check className="h-2.5 w-2.5 text-white" />
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="flex flex-col items-center space-y-1">
                <img src={team.logo} alt={team.name} className="w-8 h-8 object-contain" />
                <span className={`text-[8px] font-semibold text-center ${
                  isPreviouslyPicked ? 'text-gray-400' : isSelected ? 'text-blue-700' : 'text-gray-900'
                }`}>{team.name}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="bg-gray-100 rounded-lg p-2 mb-3">
        <p className="text-[8px] text-gray-500 uppercase tracking-wider mb-1.5 font-medium">Previously Picked</p>
        <div className="flex gap-1.5">
          {previouslyPicked.map(teamName => {
            const team = teams.find(t => t.name === teamName);
            return (
              <div key={teamName} className="flex items-center gap-1 bg-white rounded px-1.5 py-0.5">
                <img src={team?.logo} alt={teamName} className="w-4 h-4 object-contain" />
                <span className="text-[8px] text-gray-600">{teamName}</span>
              </div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-16 left-3 right-3 bg-green-500 rounded-xl p-2.5 flex items-center gap-2 shadow-lg"
          >
            <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-green-500" />
            </div>
            <span className="text-white font-medium text-xs">Pick Locked In!</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-auto pt-2 text-center">
        <span className="text-white/40 text-[10px] uppercase tracking-wider">Last Man Standing</span>
      </div>
    </div>
  );
};
